import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from 'firebase/auth';
import { FirestoreService } from './firestore';
import { tmdb } from './tmdb';

export interface WatchlistItem {
  id: number;
  type: 'tv' | 'movie';
  name: string;
  posterPath: string | null;
  backdropPath: string | null;
  overview: string;
  watchedSeasons: number[];
  lastChecked: string;
  hasNewContent: boolean;
  totalSeasons?: number;
  addedAt: string;
  lastWatched?: string;
  rating?: number;
  personalRank?: number; // 1-10 personal ranking
  notes?: string;
  genres?: number[];
  // New content details
  newSeasonNumber?: number; // The new season that's available
  nextEpisode?: {
    name: string;
    season: number;
    episode: number;
    airDate: string;
  };
  // Intelligent update scheduling
  status?: 'Returning Series' | 'Ended' | 'Cancelled' | 'In Production' | 'Pilot' | 'Planned';
  nextCheckDate?: string; // ISO date string for when to check next
  updateFrequency?: number; // Hours between checks
  // Manual watching status
  currentlyWatching?: boolean; // User manually marks if they're actively watching this
  // Movie watched status
  watched?: boolean; // For movies: has the user watched it
  // Newly added holding status (temporary, for movies only)
  newlyAdded?: boolean; // True for movies just added, cleared on page refresh
  // Episode-based tracking
  watchedEpisodes?: Record<number, number[]>; // season number -> array of episode numbers
  episodeWatchDates?: Record<string, string>; // "S1E1" -> ISO date string
  // Historical tracking for change detection
  previousSeasons?: number; // Last known season count to detect new seasons
  previousEpisodeCount?: Record<number, number>; // { 1: 10, 2: 13 } - season -> episode count
  lastNotificationCheck?: string; // When we last checked for notifications
  // Episode notification tracking (prevent duplicates)
  notifiedEpisodes?: string[]; // ["S1E1", "S1E2", "season-3"] - already sent notifications
  // Upcoming episode cache (reduce API calls)
  upcomingEpisodes?: Array<{
    season: number;
    episode: number;
    name: string;
    airDate: string;
  }>;
}

export interface DislikedItem {
  id: number;
  type: 'tv' | 'movie';
  name: string;
  dislikedAt: string;
  genres?: number[];
  reason?: string;
}

// Hierarchical filter types
export type TVFilter = 'tv-watching' | 'tv-in-progress' | 'tv-unwatched' | 'tv-complete' | 'tv-new' | 'tv-recently-added';
export type MovieFilter = 'movie-unwatched' | 'movie-complete' | 'movie-recently-added';
export type ActiveFilter = TVFilter | MovieFilter;

interface AppState {
  user: User | null;
  watchlist: WatchlistItem[];
  dislikedItems: DislikedItem[];
  isLoading: boolean;
  searchQuery: string;
  filterType: 'all' | 'tv' | 'movie' | 'new' | 'watching';
  sortBy: 'recently-added' | 'recently-updated' | 'name-asc' | 'name-desc' | 'rating-high' | 'rating-low' | 'seasons-most' | 'seasons-least' | 'personal-rank';
  statusFilter: string | null;
  genreFilter: number | null;

  // New hierarchical filters
  activeFilters: ActiveFilter[];

  // Actions
  setUser: (user: User | null) => void;
  setWatchlist: (watchlist: WatchlistItem[]) => void;
  addToWatchlist: (item: WatchlistItem) => void;
  removeFromWatchlist: (id: number) => void;
  updateWatchlistItem: (id: number, updates: Partial<WatchlistItem>) => void;
  markSeasonWatched: (showId: number, seasonNumber: number) => void;
  markSeasonUnwatched: (showId: number, seasonNumber: number) => void;
  markEpisodeWatched: (showId: number, seasonNumber: number, episodeNumber: number) => Promise<void>;
  markEpisodeUnwatched: (showId: number, seasonNumber: number, episodeNumber: number) => Promise<void>;
  syncWithFirestore: (userId: string) => void;
  stopFirestoreSync: () => void;
  setSearchQuery: (query: string) => void;
  setFilterType: (type: 'all' | 'tv' | 'movie' | 'new' | 'watching') => void;
  setSortBy: (sort: 'recently-added' | 'recently-updated' | 'name-asc' | 'name-desc' | 'rating-high' | 'rating-low' | 'seasons-most' | 'seasons-least' | 'personal-rank') => void;
  setIsLoading: (loading: boolean) => void;
  checkForNewContent: (showId: number) => void;
  setStatusFilter: (status: string | null) => void;
  setGenreFilter: (genre: number | null) => void;

  // New hierarchical filter actions
  toggleFilter: (filter: ActiveFilter) => void;
  setFilters: (filters: ActiveFilter[]) => void;
  selectAllTV: () => void;
  selectAllMovies: () => void;
  clearAllFilters: () => void;

  // Dislike actions
  addToDisliked: (item: DislikedItem) => void;
  removeFromDisliked: (id: number) => void;
  isDisliked: (id: number) => boolean;

  // Ranking actions
  setPersonalRank: (id: number, rank: number) => Promise<void>;

  // Watching status
  toggleCurrentlyWatching: (id: number) => void;

  // Movie watched status
  toggleMovieWatched: (id: number) => void;

  // Clear newly added status
  clearNewlyAddedStatus: () => void;
}

let firestoreUnsubscribe: (() => void) | null = null;

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      watchlist: [],
      dislikedItems: [],
      isLoading: false,
      searchQuery: '',
      filterType: 'all',
      sortBy: 'name-asc',
      statusFilter: null,
      genreFilter: null,
      activeFilters: [], // Start with no filters (show all)

      setUser: (user) => {
        set({ user });

        if (user) {
          // Start syncing with Firestore when user logs in
          get().syncWithFirestore(user.uid);
        } else {
          // Stop syncing when user logs out
          get().stopFirestoreSync();
          set({ watchlist: [], dislikedItems: [] });
        }
      },

      setWatchlist: (watchlist) => set({ watchlist }),

      addToWatchlist: async (item) => {
        const state = get();
        const user = state.user;

        // Mark movies as newly added (temporary holding status)
        const itemWithNewlyAdded = item.type === 'movie'
          ? { ...item, newlyAdded: true }
          : item;

        // Update local state immediately
        set((state) => ({
          watchlist: [...state.watchlist, itemWithNewlyAdded],
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.saveWatchlistItem(user.uid, itemWithNewlyAdded);
          } catch (error) {
            // Revert local state on error
            set((state) => ({
              watchlist: state.watchlist.filter((i) => i.id !== item.id),
            }));
            throw error;
          }
        }
      },

      removeFromWatchlist: async (id) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.filter((item) => item.id !== id),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.removeWatchlistItem(user.uid, id);
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      updateWatchlistItem: async (id, updates) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === id ? { ...item, ...updates } : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.updateWatchlistItem(user.uid, id, updates);
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      syncWithFirestore: (userId: string) => {
        // Stop any existing subscription
        if (firestoreUnsubscribe) {
          firestoreUnsubscribe();
        }

        // Start new subscription
        firestoreUnsubscribe = FirestoreService.subscribeToWatchlist(userId, (items) => {
          set({ watchlist: items });
        });
      },

      stopFirestoreSync: () => {
        if (firestoreUnsubscribe) {
          firestoreUnsubscribe();
          firestoreUnsubscribe = null;
        }
      },

      markSeasonWatched: async (showId, seasonNumber) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;
        const item = state.watchlist.find(i => i.id === showId && i.type === 'tv');

        if (!item) return;

        // Auto-mark all previous seasons as watched
        const seasonsToMark = [];
        for (let i = 1; i <= seasonNumber; i++) {
          seasonsToMark.push(i);
        }

        // Fetch episode data for each season being marked
        const watchedEpisodes = item.watchedEpisodes || {};
        const episodeWatchDates = item.episodeWatchDates || {};

        try {
          for (const season of seasonsToMark) {
            // Fetch season details from TMDB
            const seasonData = await tmdb.getSeason(showId, season);
            const episodeCount = seasonData.episodes?.length || 0;

            // Check if all episodes are already marked as watched
            const currentWatchedEps = watchedEpisodes[season] || [];
            const allEpisodesWatched = currentWatchedEps.length === episodeCount && episodeCount > 0;

            if (allEpisodesWatched) {
              // Skip if all episodes are already marked
              continue;
            }

            // Mark all episodes as watched
            const episodeNumbers = Array.from({ length: episodeCount }, (_, i) => i + 1);
            watchedEpisodes[season] = episodeNumbers;

            // Record watch dates for all episodes
            const now = new Date().toISOString();
            episodeNumbers.forEach(epNum => {
              episodeWatchDates[`S${season}E${epNum}`] = now;
            });
          }
        } catch (error) {
          // Continue with season marking even if episode fetch fails
        }

        // Check if the newly marked season clears the new content flag
        // Only clear if we've watched the specific new season that was flagged
        const shouldClearNewContent = item?.newSeasonNumber
          ? seasonNumber >= item.newSeasonNumber
          : false; // Don't clear if there's no new season number

        // Check if this completes the show
        const newWatchedSeasons = [...new Set([...item.watchedSeasons, ...seasonsToMark])];
        const isNowCompleted = item?.totalSeasons && newWatchedSeasons.length >= item.totalSeasons;

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === showId && item.type === 'tv'
              ? {
                  ...item,
                  watchedSeasons: newWatchedSeasons,
                  watchedEpisodes,
                  episodeWatchDates,
                  lastWatched: new Date().toISOString(),
                  // Only clear new content if we actually watched the new season
                  hasNewContent: shouldClearNewContent ? false : item.hasNewContent,
                  newSeasonNumber: shouldClearNewContent ? undefined : item.newSeasonNumber,
                  // Keep nextEpisode data even when all seasons are watched - it shows upcoming episodes
                  // Only clear nextEpisode if we specifically cleared hasNewContent
                  nextEpisode: shouldClearNewContent ? undefined : item.nextEpisode,
                  // Automatically remove from "Watching Now" when completed
                  currentlyWatching: isNowCompleted ? false : item.currentlyWatching,
                }
              : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.markSeasonWatched(user.uid, showId, seasonNumber);
            // Also sync the currentlyWatching flag if it changed
            if (isNowCompleted && item?.currentlyWatching) {
              await FirestoreService.updateWatchlistItem(user.uid, showId, { currentlyWatching: false });
            }
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      markSeasonUnwatched: async (showId, seasonNumber) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;

        // Update local state immediately - clear watched seasons AND episode data
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === showId && item.type === 'tv'
              ? {
                  ...item,
                  watchedSeasons: item.watchedSeasons.filter((s) => s !== seasonNumber),
                  // Clear episode data for this season
                  watchedEpisodes: item.watchedEpisodes
                    ? Object.fromEntries(
                        Object.entries(item.watchedEpisodes).filter(([s]) => parseInt(s) !== seasonNumber)
                      )
                    : {},
                  // Clear episode watch dates for this season
                  episodeWatchDates: item.episodeWatchDates
                    ? Object.fromEntries(
                        Object.entries(item.episodeWatchDates).filter(([key]) => !key.startsWith(`S${seasonNumber}E`))
                      )
                    : {},
                }
              : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.markSeasonUnwatched(user.uid, showId, seasonNumber);
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      markEpisodeWatched: async (showId, seasonNumber, episodeNumber) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;
        const item = state.watchlist.find(i => i.id === showId && i.type === 'tv');

        if (!item) return;

        // Initialize episode data if needed
        const watchedEpisodes = item.watchedEpisodes || {};
        const seasonEpisodes = watchedEpisodes[seasonNumber] || [];
        const episodeWatchDates = item.episodeWatchDates || {};

        // Auto-mark all previous episodes (1 through episodeNumber)
        const episodesToMark = [];
        for (let i = 1; i <= episodeNumber; i++) {
          if (!seasonEpisodes.includes(i)) {
            episodesToMark.push(i);
          }
        }

        if (episodesToMark.length > 0) {
          // Add all episodes from 1 to episodeNumber
          const newSeasonEpisodes = [...new Set([...seasonEpisodes, ...episodesToMark])].sort((a, b) => a - b);
          const newWatchedEpisodes = { ...watchedEpisodes, [seasonNumber]: newSeasonEpisodes };

          // Record watch dates for all newly marked episodes
          const now = new Date().toISOString();
          const newEpisodeWatchDates = { ...episodeWatchDates };
          episodesToMark.forEach(epNum => {
            const episodeKey = `S${seasonNumber}E${epNum}`;
            if (!newEpisodeWatchDates[episodeKey]) {
              newEpisodeWatchDates[episodeKey] = now;
            }
          });

          // Check if this completes the season - need to fetch episode count from TMDB
          // For now, we'll update watchedSeasons separately when UI detects season completion

          // Update local state immediately
          set((state) => ({
            watchlist: state.watchlist.map((item) =>
              item.id === showId && item.type === 'tv'
                ? {
                    ...item,
                    watchedEpisodes: newWatchedEpisodes,
                    episodeWatchDates: newEpisodeWatchDates,
                    lastWatched: new Date().toISOString(),
                  }
                : item
            ),
          }));

          // Sync with Firestore if user is logged in - batch update instead of individual calls
          if (user) {
            try {
              // Update the entire watchedEpisodes and episodeWatchDates in a single Firestore call
              await FirestoreService.updateWatchlistItem(user.uid, showId, {
                watchedEpisodes: newWatchedEpisodes,
                episodeWatchDates: newEpisodeWatchDates,
                lastWatched: now,
              });
            } catch (error) {
              // Revert local state on error
              set({ watchlist: originalWatchlist });
              throw error;
            }
          }
        }
      },

      markEpisodeUnwatched: async (showId, seasonNumber, episodeNumber) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;
        const item = state.watchlist.find(i => i.id === showId && i.type === 'tv');

        if (!item || !item.watchedEpisodes) return;

        const watchedEpisodes = item.watchedEpisodes;
        const seasonEpisodes = watchedEpisodes[seasonNumber] || [];
        const episodeWatchDates = item.episodeWatchDates || {};

        // Remove episode from watched list
        const newSeasonEpisodes = seasonEpisodes.filter(ep => ep !== episodeNumber);
        const newWatchedEpisodes = { ...watchedEpisodes, [seasonNumber]: newSeasonEpisodes };

        // Remove watch date
        const episodeKey = `S${seasonNumber}E${episodeNumber}`;
        const newEpisodeWatchDates = { ...episodeWatchDates };
        delete newEpisodeWatchDates[episodeKey];

        // Remove season from watchedSeasons if it was there
        const newWatchedSeasons = item.watchedSeasons.filter(s => s !== seasonNumber);

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === showId && item.type === 'tv'
              ? {
                  ...item,
                  watchedEpisodes: newWatchedEpisodes,
                  episodeWatchDates: newEpisodeWatchDates,
                  watchedSeasons: newWatchedSeasons,
                }
              : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.markEpisodeUnwatched(user.uid, showId, seasonNumber, episodeNumber);
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      setSearchQuery: (query) => set({ searchQuery: query }),

      setFilterType: (type) => set({ filterType: type }),

      setSortBy: (sort) => set({ sortBy: sort }),

      setIsLoading: (loading) => set({ isLoading: loading }),

      setStatusFilter: (status) => set({ statusFilter: status }),

      setGenreFilter: (genre) => set({ genreFilter: genre }),

      checkForNewContent: (showId) =>
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === showId
              ? { ...item, lastChecked: new Date().toISOString() }
              : item
          ),
        })),

      // Dislike actions
      addToDisliked: (item) =>
        set((state) => ({
          dislikedItems: [...state.dislikedItems, item],
        })),

      removeFromDisliked: (id) =>
        set((state) => ({
          dislikedItems: state.dislikedItems.filter((item) => item.id !== id),
        })),

      isDisliked: (id) => {
        const state = get();
        return state.dislikedItems.some((item) => item.id === id);
      },

      // Ranking actions
      setPersonalRank: async (id, rank) => {
        const state = get();
        const user = state.user;
        const originalWatchlist = state.watchlist;

        // Convert 0 to null for proper deletion in Firestore (deleteField)
        // Use undefined for local state to remove the property
        const localRank = rank === 0 ? undefined : rank;
        const firestoreRank = rank === 0 ? null : rank;

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === id ? { ...item, personalRank: localRank } : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.updateWatchlistItem(user.uid, id, { personalRank: firestoreRank });
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      // Watching status
      toggleCurrentlyWatching: async (id) => {
        const state = get();
        const user = state.user;
        const item = state.watchlist.find(i => i.id === id);
        const originalWatchlist = state.watchlist;

        if (!item) return;

        const newStatus = !item.currentlyWatching;

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === id ? { ...item, currentlyWatching: newStatus } : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.updateWatchlistItem(user.uid, id, { currentlyWatching: newStatus });
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      // Movie watched status
      toggleMovieWatched: async (id) => {
        const state = get();
        const user = state.user;
        const item = state.watchlist.find(i => i.id === id && i.type === 'movie');
        const originalWatchlist = state.watchlist;

        if (!item) return;

        const newStatus = !item.watched;

        // Update local state immediately
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.id === id && item.type === 'movie'
              ? { ...item, watched: newStatus, lastWatched: newStatus ? new Date().toISOString() : item.lastWatched }
              : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            await FirestoreService.updateWatchlistItem(user.uid, id, {
              watched: newStatus,
              lastWatched: newStatus ? new Date().toISOString() : item.lastWatched
            });
          } catch (error) {
            // Revert local state on error
            set({ watchlist: originalWatchlist });
            throw error;
          }
        }
      },

      // Clear newly added status (called on page load/refresh)
      clearNewlyAddedStatus: async () => {
        const state = get();
        const user = state.user;

        // Find all movies with newlyAdded flag
        const moviesToUpdate = state.watchlist.filter(
          item => item.type === 'movie' && item.newlyAdded
        );

        if (moviesToUpdate.length === 0) return;

        // Update local state - clear newlyAdded flag
        set((state) => ({
          watchlist: state.watchlist.map((item) =>
            item.type === 'movie' && item.newlyAdded
              ? { ...item, newlyAdded: false }
              : item
          ),
        }));

        // Sync with Firestore if user is logged in
        if (user) {
          try {
            // Update all newly added movies in Firestore
            await Promise.all(
              moviesToUpdate.map(item =>
                FirestoreService.updateWatchlistItem(user.uid, item.id, { newlyAdded: false })
              )
            );
          } catch (error) {
          }
        }
      },

      // New hierarchical filter actions
      toggleFilter: (filter) => {
        set((state) => {
          const isActive = state.activeFilters.includes(filter);
          return {
            activeFilters: isActive
              ? state.activeFilters.filter(f => f !== filter)
              : [...state.activeFilters, filter]
          };
        });
      },

      setFilters: (filters) => {
        set({ activeFilters: filters });
      },

      selectAllTV: () => {
        const tvFilters: TVFilter[] = ['tv-watching', 'tv-in-progress', 'tv-unwatched', 'tv-complete', 'tv-new', 'tv-recently-added'];
        set((state) => {
          // Get current movie filters
          const movieFilters = state.activeFilters.filter(f => f.startsWith('movie-')) as MovieFilter[];
          // Combine with all TV filters
          return { activeFilters: [...tvFilters, ...movieFilters] };
        });
      },

      selectAllMovies: () => {
        const movieFilters: MovieFilter[] = ['movie-unwatched', 'movie-complete', 'movie-recently-added'];
        set((state) => {
          // Get current TV filters
          const tvFilters = state.activeFilters.filter(f => f.startsWith('tv-')) as TVFilter[];
          // Combine with all movie filters
          return { activeFilters: [...tvFilters, ...movieFilters] };
        });
      },

      clearAllFilters: () => {
        set({ activeFilters: [] });
      },
    }),
    {
      name: 'epify-storage',
      partialize: (state) => ({
        watchlist: state.watchlist,
        dislikedItems: state.dislikedItems,
        filterType: state.filterType,
        sortBy: state.sortBy,
        statusFilter: state.statusFilter,
        genreFilter: state.genreFilter,
        activeFilters: state.activeFilters,
      }),
    }
  )
);